import {
  type ColumnsType,
  type ColumnType,
  type TableProps,
} from 'antd/es/table';
import { useMemo, useState } from 'react';
import { addColumnPicker, SETTING_COLUMN_KEY } from './addColumnPicker.tsx';
import { ColumnPicker } from './ColumnPicker.tsx';
import { ColumnPickerLocalState } from './ColumnPickerLocalState.ts';
import { type ColumnPickerConfig } from './form/ColumnPickerSchema.tsx';
import { type PickableColumnConfig } from './IPickableColumn.tsx';
import { PickableColumnUtils } from './utils/PickableColumnUtils.ts';

const COLUMN_BASE_WIDTH = 150;

/**
 * Hook che gestisce le colonne configurabili di un `TableData`.
 */
export function usePickedColumns(
  inputColumns: Array<ColumnType<any>>,
  /** Configurazione colonne tabella */
  config: PickableColumnConfig<any>,
) {
  const [pickableConfig, setPickableConfig] = useState<ColumnPickerConfig>(
    ColumnPickerLocalState.read(config?.id),
  );

  // le colonne si aggiornano quando cambia la configurazione nel ColumnPicker
  const columns = useMemo(() => {
    const allInputColumns = [
      ...(inputColumns ?? []),
      ...(config?.extraColumns ?? []),
    ];

    // troviamo la colonna azioni a cui aggiungere il pulsante per il ColumnPicker
    const { otherColumns, settingColumn } =
      PickableColumnUtils.divideSettingColumn(inputColumns);

    if (!PickableColumnUtils.isEnabled() || config?.pickerDisabled) {
      return PickableColumnUtils.mapDefaultColumns({
        columns: otherColumns,
        settingColumn,
      });
    }

    // Controlliamo la configurazione salvata delle colonne disponibili
    const nextColumns: ColumnsType<any> =
      // se ci sono colonne disponibili e la configurazione è stata caricata
      pickableConfig.columns?.length
        ? PickableColumnUtils.mapPickableColumnsFromConfig({
            allInputColumns,
            pickableConfig,
            settingColumn,
          })
        : PickableColumnUtils.mapDefaultColumns({
            columns: otherColumns,
            settingColumn,
          });

    // aggiungiamo il componente per la configurazione delle colonne nella colonna delle azioni
    return addColumnPicker(
      nextColumns?.filter(c => c.key !== undefined) ?? [],
      <ColumnPicker
        // passiamo al componente tutte le colonne disponibili passate alla tabella (statiche + dinamiche configurate)
        columns={inputColumns}
        // e l'id per trovare/salvare la configurazione
        id={config?.id}
        // e la funzione aggiornare la configurazione
        onChange={nextConfig => setPickableConfig(nextConfig)}
        // e gli eventuali componenti aggiuntivi da mostrare nell'intestazione del drawer di configurazione
        extra={config?.extra}
        // Passiamo tutte le colonne dinamiche aggiuntive (e.g. tag semantici)
        extraColumns={config?.extraColumns}
      />,
    );
  }, [
    config?.extra,
    config?.extraColumns,
    config?.id,
    config?.pickerDisabled,
    inputColumns,
    pickableConfig,
  ]);

  // Solo la colonna delle impostazioni è fixed
  const fixedColumnProps = useMemo(() => ({}), []); //getFixedColumnTableProps(columns, config?.id);

  return { columns, fixedColumnProps };
}

/**
 * funzione aumentare la larghezza della tabella per lo sccroll orizzotale
 * se ci sono colonne fisse
 * @deprecated Al non usiamo nessuna proprietà per la tabella in maniera dinamica.
 */
function getFixedColumnTableProps<T>(
  columns?: Array<ColumnType<T>>,
  id?: string,
): Partial<TableProps<T>> {
  if (!id) {
    return { scroll: undefined, bordered: false };
  }
  return columns
    ?.filter(c => c.key != SETTING_COLUMN_KEY)
    ?.some(c => c.fixed && !c.showInExpandedRow)
    ? { scroll: { x: columns.length * COLUMN_BASE_WIDTH }, bordered: true }
    : { scroll: undefined, bordered: false };
}
